import React, { createContext, useContext, useMemo, useState } from 'react';
import {
  Dialog,
  DialogProps,
  GenericLinkType,
  TextBlockChildren,
} from '@sato/components';

import { useTranslate } from '@hooks/useTranslate';

type CustomDialogProps<T extends GenericLinkType = 'a'> = Omit<
  // Enforce body prop to be used in useDialog props
  DialogProps<T> & { body: TextBlockChildren; children?: never },
  'isOpen'
>;

interface DialogContextType {
  openDialog: <T extends GenericLinkType = 'a'>(
    dialog: CustomDialogProps<T>
  ) => void;
  closeDialog: () => void;
}

const DialogContext = createContext<DialogContextType>({
  openDialog: () => undefined,
  closeDialog: () => undefined,
});

export const DialogProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  // Use any so that setDialog can be set with any link type in openDialog
  const [dialog, setDialog] = useState<CustomDialogProps<any> | null>(null);

  const contextValue = useMemo(() => {
    const openDialog = <T extends GenericLinkType = 'a'>(
      dialogProps: CustomDialogProps<T>
    ) => setDialog(dialogProps);

    const closeDialog = () => setDialog(null);

    return { openDialog, closeDialog };
  }, [setDialog]);

  return (
    <DialogContext.Provider value={contextValue}>
      {children}

      <Dialog
        heading=""
        actionButton={{}}
        {...dialog}
        isOpen={!!dialog}
        layout={dialog?.layout ?? 'column'}
        onOpenChange={(isOpen) => {
          dialog?.onOpenChange?.(isOpen);

          if (!isOpen) setDialog(null);
        }}
      />
    </DialogContext.Provider>
  );
};

export const useDialog = () => {
  const { t } = useTranslate();

  const { openDialog, closeDialog } = useContext(DialogContext);

  return {
    openDialog,
    closeDialog,
    openErrorDialog: ({
      onClose,
      body,
      heading,
    }: {
      onClose?: () => void;
      body?: string;
      heading?: string;
    } = {}) =>
      openDialog({
        actionButton: {
          onClick: onClose,
          children: t('ok'),
        },
        heading: heading ?? t('error.error_title'),
        body: body ?? t('error'),
      }),
  };
};
